import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  LayoutDashboard,
  BookOpen,
  ClipboardList,
  FileCheck2,
  UserCircle2,
  Settings,
  History,
  LogOut,
  Pin,
  PinOff,
} from "lucide-react";
import { getExamV3Flags } from "../services/exam.service";
import { cn } from "./cn";
import { BrandIcon, useBrand } from "./BrandMark";

/**
 * TeacherSidebar — mirrors the student Sidebar's interactivity (hover-expand,
 * ⌘1–⌘5 shortcuts, pin-to-lock, pulsing active accent, micro-bounce) with the
 * teacher's section set.
 */

// Timetable, Virtual Meeting, Grievances and Help are not here: they are the
// LMS's, and their APIs (/meetings, /conference, /grievances, /tickets) are
// outside the exam build (backend/config/productScope.js) — each opened to a 404.
const NAV = [
  { id: "dashboard",       label: "Dashboard",       tKey: "sidebar.dashboard",       icon: LayoutDashboard, to: "/teacher/dashboard",
    light: "bg-indigo-100 text-indigo-600",   dark: "dark:bg-indigo-500/15 dark:text-indigo-300",
    active: "from-indigo-500 to-violet-500",
    activeGlow: "shadow-[0_10px_28px_-10px_rgba(99,102,241,0.55)]" },
  { id: "courses",         label: "My Courses",      tKey: "sidebar.my_courses",      icon: BookOpen,        to: "/teacher/courses",
    matches: ["/teacher/course/", "/teacher/lesson/"],
    light: "bg-emerald-100 text-emerald-600", dark: "dark:bg-emerald-500/15 dark:text-emerald-300",
    active: "from-emerald-500 to-teal-500",
    activeGlow: "shadow-[0_10px_28px_-10px_rgba(16,185,129,0.55)]" },
  { id: "exams",           label: "Examinations",    tKey: "sidebar.examinations",    icon: ClipboardList,   to: "/teacher/exams-home",
    matches: ["/teacher/exams/", "/teacher/exam/", "/teacher/question-bank/", "/teacher/groups/"],
    light: "bg-amber-100 text-amber-600",     dark: "dark:bg-amber-500/15 dark:text-amber-300",
    active: "from-amber-500 to-orange-500",
    activeGlow: "shadow-[0_10px_28px_-10px_rgba(245,158,11,0.55)]" },
  { id: "activity-log",    label: "Activity Log",    tKey: "sidebar.activity_log",    icon: History,         to: "/teacher/activity-log",
    light: "bg-orange-100 text-orange-600",   dark: "dark:bg-orange-500/15 dark:text-orange-300",
    active: "from-orange-500 to-amber-500",
    activeGlow: "shadow-[0_10px_28px_-10px_rgba(249,115,22,0.55)]" },
];

// Teacher evaluator surface (EXAM_OFFLINE) — inserted after "exams" only when
// the backend flag is on. Ship-dark like the other offline-exam surfaces: the
// backend is the single source of truth (/exam-v3/flags), so with the flag
// off this item never renders and the nav is byte-identical to before.
const EVALUATIONS_NAV_ITEM = {
  id: "evaluations", label: "My Evaluations", tKey: "sidebar.my_evaluations", icon: FileCheck2, to: "/teacher/evaluations",
  light: "bg-teal-100 text-teal-600",       dark: "dark:bg-teal-500/15 dark:text-teal-300",
  active: "from-teal-500 to-emerald-500",
  activeGlow: "shadow-[0_10px_28px_-10px_rgba(20,184,166,0.55)]",
};

const FOOTER_NAV = [
  { id: "profile",  label: "Profile",          tKey: "sidebar.profile",          icon: UserCircle2, to: "/profile" },
  { id: "account",  label: "Account settings", tKey: "sidebar.account_settings", icon: Settings,    to: "/teacher/profile/account" },
];

function isActivePath(itemTo, currentPath, matches) {
  if (!itemTo) return false;
  if (itemTo === currentPath) return true;
  const base = itemTo.split("?")[0];
  if (currentPath.startsWith(base + "/")) return true;
  if (Array.isArray(matches)) {
    for (const m of matches) {
      if (currentPath === m || currentPath.startsWith(m)) return true;
    }
  }
  return false;
}

// The sub-label's translation key (the caller translates it).
function deriveSubLabel(sectionId, currentPath) {
  if (sectionId === "courses" && currentPath.startsWith("/teacher/course/")) {
    return currentPath.endsWith("/lessons") ? "sidebar.sub_lessons" : "sidebar.sub_course_grades";
  }
  if (sectionId === "exams") {
    if (currentPath.includes("/create"))     return "sidebar.sub_creating_exam";
    if (currentPath.includes("/grade"))      return "sidebar.sub_grading";
    if (currentPath.includes("/live"))       return "sidebar.sub_live_proctoring";
    if (currentPath.includes("/proctoring")) return "sidebar.sub_proctoring_report";
    if (currentPath.includes("/question-bank")) return "sidebar.sub_question_bank";
    if (currentPath.match(/\/teacher\/exam\/[^/]+$/)) return "sidebar.sub_exam_detail";
  }
  if (sectionId === "evaluations" && currentPath.includes("/mark")) return "sidebar.sub_marking_script";
  return null;
}

function isMac() {
  if (typeof navigator === "undefined") return false;
  return /Mac|iPod|iPhone|iPad/.test(navigator.platform || "");
}

function ShortcutKey({ digit, dim }) {
  const mod = isMac() ? "⌘" : "Ctrl";
  return (
    <span
      className={cn(
        "inline-flex items-center gap-0.5 ml-auto text-[9px] font-semibold ux-tabular tracking-wider rounded-md px-1.5 py-0.5",
        dim
          ? "bg-white/15 text-white/80"
          : "ux-surface-2 ux-text-muted border ux-border"
      )}
      aria-hidden
    >
      <span>{mod}</span>
      <span>{digit}</span>
    </span>
  );
}

function NavItem({ item, active, subLabel, shortcutDigit }) {
  const Icon = item.icon;
  const { t } = useTranslation();
  const label = item.tKey ? t(item.tKey) : item.label;
  return (
    <Link
      to={item.to}
      title={subLabel ? `${label} · ${subLabel}` : label}
      className={cn(
        "ux-ring relative flex items-center gap-2.5 h-11 w-full",
        "rounded-2xl transition-[width,background-color,box-shadow,transform] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]",
        "active:scale-[0.97]",
        active && "group-hover/sidebar:" + item.activeGlow
      )}
    >
      {active && (
        <span
          className={cn(
            "pointer-events-none absolute -left-[14px] top-1/2 -translate-y-1/2 w-[5px] h-8 rounded-r-full bg-gradient-to-b",
            "shadow-[2px_0_10px_-2px_currentColor]",
            "ux-pulse-soft",
            item.active
          )}
          aria-hidden
        />
      )}
      {active && (
        <span
          className={cn(
            "pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-r opacity-0 group-hover/sidebar:opacity-100 group-data-[pinned=true]/sidebar:!opacity-100 transition-opacity duration-300",
            item.active
          )}
        />
      )}
      <span
        className={cn(
          "relative flex items-center justify-center w-10 h-10 rounded-xl flex-none transition-all duration-300 will-change-transform",
          "group-hover/nav-item:scale-[1.08] group-hover/nav-item:-rotate-[3deg]",
          active
            ? cn(
                "bg-gradient-to-br text-white ring-2 ring-white/40 dark:ring-white/15",
                item.active,
                item.activeGlow
              )
            : cn(
                item.light,
                // Written out whole ("dark:bg-…") in the item: Tailwind only builds classes it can read in
                // the source, and a class glued together here at run time compiled to nothing — the tiles stayed light.
                item.dark
              )
        )}
      >
        <Icon
          className={cn("w-[18px] h-[18px] transition", active ? "text-white" : "")}
          strokeWidth={2}
        />
        {active && subLabel && (
          <span
            className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-white border-2 border-[var(--ux-surface)] group-hover/sidebar:opacity-0 group-data-[pinned=true]/sidebar:!opacity-0 transition-opacity"
            aria-hidden
          />
        )}
      </span>

      <span
        className={cn(
          "relative pr-3 flex items-center flex-1 min-w-0 whitespace-nowrap overflow-hidden",
          "opacity-0 -translate-x-1 group-hover/sidebar:opacity-100 group-hover/sidebar:translate-x-0 group-data-[pinned=true]/sidebar:!opacity-100 group-data-[pinned=true]/sidebar:!translate-x-0",
          "transition-[opacity,transform] duration-300 ease-out",
          active ? "text-white" : "ux-text"
        )}
      >
        <span className="flex flex-col leading-tight min-w-0">
          <span className="text-[13px] font-semibold truncate">{label}</span>
          {active && subLabel && (
            <span className="text-[10px] font-medium opacity-90 mt-0.5 truncate">
              · {subLabel}
            </span>
          )}
        </span>
        {shortcutDigit && <ShortcutKey digit={shortcutDigit} dim={active} />}
      </span>

      <span className="pointer-events-none absolute left-full ml-3 px-2.5 py-1.5 rounded-lg ux-surface border ux-border ux-shadow-md text-[12px] font-medium ux-text whitespace-nowrap opacity-0 translate-x-[-4px] group-hover/nav-item:opacity-100 group-hover/nav-item:translate-x-0 group-hover/sidebar:opacity-0 group-data-[pinned=true]/sidebar:!opacity-0 transition z-50">
        <span className="inline-flex items-center gap-1.5">
          {label}
          {active && subLabel && <span className="ux-text-muted">· {subLabel}</span>}
          {shortcutDigit && (
            <span className="ml-1 ux-surface-2 ux-text-muted border ux-border rounded-md px-1.5 py-0.5 text-[9px] font-semibold tracking-wider ux-tabular">
              {isMac() ? "⌘" : "Ctrl"}{shortcutDigit}
            </span>
          )}
        </span>
      </span>
    </Link>
  );
}

function MinimalItem({ item, active }) {
  const Icon = item.icon;
  const { t } = useTranslation();
  const label = item.tKey ? t(item.tKey) : item.label;
  return (
    <Link
      to={item.to}
      title={label}
      className={cn(
        "ux-ring relative flex items-center gap-2.5 h-9 w-full rounded-xl transition-[width,background-color,transform] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] active:scale-[0.97]",
        active ? "ux-surface-2 ux-text" : "ux-text-muted hover:ux-text hover:ux-surface-2"
      )}
    >
      <span className="flex items-center justify-center w-10 h-9 flex-none group-hover/nav-item:scale-[1.08] transition">
        <Icon className="w-4 h-4" strokeWidth={2} />
      </span>
      <span className="relative pr-3 text-[12px] font-medium whitespace-nowrap overflow-hidden opacity-0 -translate-x-1 group-hover/sidebar:opacity-100 group-hover/sidebar:translate-x-0 group-data-[pinned=true]/sidebar:!opacity-100 group-data-[pinned=true]/sidebar:!translate-x-0 transition-[opacity,transform] duration-300 ease-out">
        {label}
      </span>
      <span className="pointer-events-none absolute left-full ml-3 px-2 py-1 rounded-md ux-surface border ux-border ux-shadow-sm text-[11px] ux-text-soft whitespace-nowrap opacity-0 group-hover/nav-item:opacity-100 group-hover/sidebar:opacity-0 group-data-[pinned=true]/sidebar:!opacity-0 transition z-50">
        {label}
      </span>
    </Link>
  );
}

function SectionLabel({ children }) {
  return (
    <div className="relative w-full overflow-hidden h-3 mt-1 mb-0.5">
      <span className="absolute left-3 top-0 text-[10px] font-semibold uppercase tracking-[0.12em] ux-text-muted whitespace-nowrap opacity-0 -translate-x-1 group-hover/sidebar:opacity-100 group-hover/sidebar:translate-x-0 group-data-[pinned=true]/sidebar:!opacity-100 group-data-[pinned=true]/sidebar:!translate-x-0 transition-[opacity,transform] duration-300">
        {children}
      </span>
    </div>
  );
}

function PinToggle({ pinned, onToggle }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onToggle}
      title={pinned ? t("sidebar.unpin") : t("sidebar.pin")}
      aria-label={pinned ? t("sidebar.unpin") : t("sidebar.pin")}
      className={cn(
        "ux-ring absolute top-4 right-2 z-50 w-7 h-7 rounded-lg flex items-center justify-center transition",
        "opacity-0 group-hover/sidebar:opacity-100 group-data-[pinned=true]/sidebar:!opacity-100",
        pinned
          ? "ux-accent-bg text-white shadow-[0_4px_12px_-3px_rgba(79,70,229,0.5)]"
          : "ux-surface-2 ux-text-muted hover:ux-text"
      )}
    >
      {pinned ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}
    </button>
  );
}

export function TeacherSidebar({ userName = "Teacher", role = "Teacher", onSignOut }) {
  const brand = useBrand();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // EXAM_OFFLINE gate for the My-Evaluations item (backend is the single
  // source of truth; endpoint down / flag off → item stays hidden).
  const [offlineExamOn, setOfflineExamOn] = useState(false);
  useEffect(() => {
    getExamV3Flags()
      .then((d) => setOfflineExamOn(!!d?.data?.flags?.EXAM_OFFLINE))
      .catch(() => setOfflineExamOn(false));
  }, []);
  const nav = useMemo(() => {
    if (!offlineExamOn) return NAV;
    const examsIdx = NAV.findIndex((i) => i.id === "exams");
    const at = examsIdx >= 0 ? examsIdx + 1 : NAV.length;
    return [...NAV.slice(0, at), EVALUATIONS_NAV_ITEM, ...NAV.slice(at)];
  }, [offlineExamOn]);

  const [pinned, setPinned] = useState(() => {
    try { return localStorage.getItem("ux-teacher-sidebar-pinned") === "1"; } catch { return false; }
  });
  const togglePin = () => {
    setPinned((p) => {
      const next = !p;
      try { localStorage.setItem("ux-teacher-sidebar-pinned", next ? "1" : "0"); } catch {}
      try { window.dispatchEvent(new CustomEvent("ux-sidebar-pin", { detail: next })); } catch {}
      return next;
    });
  };

  useEffect(() => {
    const onKey = (e) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      if (e.altKey || e.shiftKey) return;
      const idx = Number(e.key) - 1;
      if (Number.isInteger(idx) && idx >= 0 && idx < nav.length) {
        e.preventDefault();
        navigate(nav[idx].to);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate, nav]);

  // Expanded = hovered (transient) OR pinned (sticky). Width is React-driven.
  const [hovered, setHovered] = useState(false);
  const expanded = pinned || hovered;

  return (
    <>
      <input id="ux-sidebar-pin" type="checkbox" className="peer/pin sr-only" checked={pinned} readOnly tabIndex={-1} aria-hidden />

      <aside
        data-pinned={pinned ? "true" : "false"}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className={cn(
          "group/sidebar fixed top-0 left-0 z-40 h-screen flex flex-col py-3 gap-1.5",
          expanded ? "w-[216px] min-w-[216px] max-w-[216px]" : "w-[64px] min-w-[64px] max-w-[64px]",
          "transition-[width,box-shadow] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]",
          "bg-[color-mix(in_oklab,var(--ux-surface),transparent_5%)] backdrop-blur-xl data-[pinned=true]:bg-[var(--ux-surface)] data-[pinned=true]:backdrop-blur-none border-r ux-border",
          "hover:shadow-[18px_0_48px_-24px_rgba(15,23,42,0.18)] data-[pinned=true]:shadow-[18px_0_48px_-24px_rgba(15,23,42,0.18)]"
        )}
      >
        <span className="pointer-events-none absolute top-12 bottom-12 right-0 w-px bg-gradient-to-b from-transparent via-indigo-400/40 to-transparent opacity-0 group-hover/sidebar:opacity-100 group-data-[pinned=true]/sidebar:!opacity-100 transition-opacity duration-500" />

        <PinToggle pinned={pinned} onToggle={togglePin} />

        {/* Brand */}
        <Link
          to="/teacher/dashboard"
          className="group/brand relative flex items-center gap-2.5 px-3 mb-1 h-10"
        >
          <div className="relative w-9 h-9 rounded-2xl bg-white flex items-center justify-center shadow-[0_10px_28px_-10px_rgba(79,70,229,0.45)] flex-none transition-transform group-hover/brand:scale-105">
            <BrandIcon className="w-6 h-6" />
            <span className="absolute -bottom-1 -right-1 w-2.5 h-2.5 rounded-full bg-emerald-500 border-2 border-[var(--ux-surface)]" />
          </div>
          <div className="relative overflow-hidden">
            <div className="flex items-center gap-1.5 whitespace-nowrap opacity-0 -translate-x-1 group-hover/sidebar:opacity-100 group-hover/sidebar:translate-x-0 group-data-[pinned=true]/sidebar:!opacity-100 group-data-[pinned=true]/sidebar:!translate-x-0 transition-[opacity,transform] duration-300 ease-out">
              <span className="text-[15px] font-semibold ux-text tracking-tight">{brand.title}</span>
            </div>
            <div className="text-[10px] uppercase tracking-[0.14em] ux-text-muted whitespace-nowrap opacity-0 -translate-x-1 group-hover/sidebar:opacity-100 group-hover/sidebar:translate-x-0 group-data-[pinned=true]/sidebar:!opacity-100 group-data-[pinned=true]/sidebar:!translate-x-0 transition-[opacity,transform] duration-300 ease-out delay-[40ms]">
              {t("sidebar.faculty_portal")}
            </div>
          </div>
        </Link>

        <div className="mx-3 h-px ux-border-strong" style={{ background: "var(--ux-border)" }} />

        <SectionLabel>{t("sidebar.section_main")}</SectionLabel>
        <nav className="flex flex-col gap-1 px-3">
          {nav.map((item, idx) => {
            const active = isActivePath(item.to, pathname, item.matches);
            return (
              <div key={item.id} className="group/nav-item">
                <NavItem
                  item={item}
                  active={active}
                  subLabel={active && deriveSubLabel(item.id, pathname) ? t(deriveSubLabel(item.id, pathname)) : null}
                  shortcutDigit={idx < 9 ? String(idx + 1) : null}
                />
              </div>
            );
          })}
        </nav>

        {/* Account links — directly under main (no wasted middle gap) */}
        <div className="mx-3 mt-2 mb-0.5 h-px" style={{ background: "var(--ux-border)" }} />
        <SectionLabel>{t("sidebar.section_account")}</SectionLabel>
        <nav className="flex flex-col gap-0.5 px-3">
          {FOOTER_NAV.map((item) => (
            <div key={item.id} className="group/nav-item">
              <MinimalItem item={item} active={isActivePath(item.to, pathname, item.matches)} />
            </div>
          ))}
        </nav>

        {/* Bottom block — sign out + user, pinned to the foot */}
        <div className="mt-auto px-3 flex flex-col gap-0.5">
          {onSignOut && (
            <div className="group/nav-item">
              <button
                onClick={onSignOut}
                title={t("common.logout")}
                className={cn(
                  "ux-ring relative w-full h-9 rounded-xl flex items-center gap-2.5",
                  "transition-[width,background-color,color,transform] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] active:scale-[0.97]",
                  "ux-text-muted hover:text-rose-600 dark:hover:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-500/10"
                )}
              >
                <span className="flex items-center justify-center w-10 h-9 flex-none group-hover/nav-item:scale-[1.08] transition">
                  <LogOut className="w-4 h-4" />
                </span>
                <span className="relative pr-3 text-[12px] font-medium whitespace-nowrap overflow-hidden opacity-0 -translate-x-1 group-hover/sidebar:opacity-100 group-hover/sidebar:translate-x-0 group-data-[pinned=true]/sidebar:!opacity-100 group-data-[pinned=true]/sidebar:!translate-x-0 transition-[opacity,transform] duration-300 ease-out">
                  {t("common.logout")}
                </span>
                <span className="pointer-events-none absolute left-full ml-3 px-2 py-1 rounded-md ux-surface border ux-border ux-shadow-sm text-[11px] ux-text-soft whitespace-nowrap opacity-0 group-hover/nav-item:opacity-100 group-hover/sidebar:opacity-0 group-data-[pinned=true]/sidebar:!opacity-0 transition z-50">
                  {t("common.logout")}
                </span>
              </button>
            </div>
          )}

          {/* User chip */}
          <div className="mt-1 mb-0.5 flex items-center gap-2.5 px-0 h-10 rounded-2xl group-hover/sidebar:px-2 group-hover/sidebar:ux-surface-2 group-data-[pinned=true]/sidebar:!px-2 group-data-[pinned=true]/sidebar:!ux-surface-2 transition-[padding,background-color] duration-300">
            <div className="w-8 h-8 rounded-xl ux-accent-bg text-white text-[12px] font-semibold flex items-center justify-center shadow-[0_6px_16px_-4px_rgba(79,70,229,0.45)] flex-none ml-1">
              {userName.slice(0, 1).toUpperCase()}
            </div>
            <div className="relative overflow-hidden">
              <div className="text-[12px] font-semibold ux-text whitespace-nowrap leading-tight opacity-0 -translate-x-1 group-hover/sidebar:opacity-100 group-hover/sidebar:translate-x-0 group-data-[pinned=true]/sidebar:!opacity-100 group-data-[pinned=true]/sidebar:!translate-x-0 transition-[opacity,transform] duration-300 ease-out">
                {userName}
              </div>
              <div className="text-[10px] ux-text-muted whitespace-nowrap leading-tight opacity-0 -translate-x-1 group-hover/sidebar:opacity-100 group-hover/sidebar:translate-x-0 group-data-[pinned=true]/sidebar:!opacity-100 group-data-[pinned=true]/sidebar:!translate-x-0 transition-[opacity,transform] duration-300 ease-out delay-[40ms]">
                {t(role === "Teacher" ? "sidebar.role_teacher" : "sidebar.role_student")}
              </div>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}
